import { updateChargeSchedule } from '@/actions/schedule'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { vehicleChargersCollection } from '@/reducers/vehicleChargers'

import { VehicleCharger } from '@/types'
import {
  isVehicleChargerChargeSchedule,
  VehicleChargerChargeSchedule,
} from '@/types/vehicleCharger/chargeSchedule'
import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { ThemeProvider } from '@material-ui/core/styles'
import theme from '@/v2/theme'
import { ConfigurableFunctionality } from '@/app/components'
import AllowChargeControlCard from '@/authenticated/components/ChargeSchedule/AllowChargeControlCard'
import { Collapse } from '@material-ui/core'
import ErrorBoundary, {
  LocationTag,
} from '@/authenticated/components/ErrorBoundary'
import PlanDetailsCard from '@/authenticated/components/ChargeSchedule/PlanDetailsCard'
import { ButtonVariant } from '@/components'
import { chargerEnrollmentCollection } from '@/reducers/deviceProgramEnrollments'
import DeviceProgramCards from '@/app/components/ProgramCards/DeviceProgramCards'
import {
  ChargingPreferencesHeader,
  ChargingPreferencesModal,
} from './CharingPreferences'
import { EVSEDeviceAlertMessage } from './EVSEDeviceAlertMessage'
import useVehicleChargerScheduleFetcher from '@/hooks/useVehicleChargerScheduleFetcher'
import ChargerEnvironmentalChargingOptions from './ChargerEnvironmentalChargingOptions'

type VehicleChargerScheduleSectionViewProps = {
  vehicleCharger: VehicleCharger
  buttonVariant?: ButtonVariant
}

function VehicleChargerScheduleSectionView(
  props: VehicleChargerScheduleSectionViewProps,
) {
  const dispatch = useAppDispatch()
  const chargeSchedule = useAppSelector((state) => {
    const schedule = state.schedule.chargeSchedules?.find(
      (schedule) => schedule.charger_id === props.vehicleCharger.id,
    )
    if (schedule && isVehicleChargerChargeSchedule(schedule)) {
      return schedule as VehicleChargerChargeSchedule
    }
  })
  const [localChargeSchedule, setLocalChargeSchedule] = useState(chargeSchedule)
  const [chargingPrefsModalOpen, setChargingPrefsModalOpen] = useState(false)

  useVehicleChargerScheduleFetcher(props.vehicleCharger.id)

  // Keep local schedule in sync with chargeSchedule
  useEffect(() => {
    if (localChargeSchedule?.id !== chargeSchedule?.id && chargeSchedule) {
      setLocalChargeSchedule(chargeSchedule)
    }
  }, [chargeSchedule, localChargeSchedule])

  const debouncedSave = useCallback(
    debounce((model) => {
      dispatch(updateChargeSchedule(model))
    }, 500),
    [],
  )

  const onChargeScheduleChange = (
    chargeSchedule: VehicleChargerChargeSchedule,
  ) => {
    setLocalChargeSchedule(chargeSchedule)
    debouncedSave(chargeSchedule)
  }

  return (
    <ThemeProvider theme={theme}>
      <EVSEDeviceAlertMessage />
      {/* <ControlledVehiclesCard vehicleChargerId={props.vehicleCharger.id} /> */}
      <ConfigurableFunctionality feature="chargingControl">
        <DeviceProgramCards />
        <AllowChargeControlCard
          chargeSchedule={localChargeSchedule}
          onChange={onChargeScheduleChange}
          canCharge={true}
        />
        <Collapse in={localChargeSchedule?.allow_charging_control}>
          <ErrorBoundary
            location={LocationTag.PlanSelectCard}
            functionalityDescription="Charger Charge Planning Overview"
            fallbackOnCard
          >
            <PlanDetailsCard
              title={
                <ChargingPreferencesHeader
                  onInfoClick={() => setChargingPrefsModalOpen(true)}
                />
              }
            >
              <PlanDetailsCard.PlanSelectForm
                chargeSchedule={localChargeSchedule}
                onChange={onChargeScheduleChange}
              >
                <PlanDetailsCard.ComparePlansPromptAndDialog
                  includeScheduleDeparture={false}
                />
              </PlanDetailsCard.PlanSelectForm>
            </PlanDetailsCard>
          </ErrorBoundary>
        </Collapse>
      </ConfigurableFunctionality>
      <ChargerEnvironmentalChargingOptions
        vehicleCharger={props.vehicleCharger}
        chargeSchedule={localChargeSchedule}
        onChargeScheduleChange={onChargeScheduleChange}
        buttonVariant={props.buttonVariant}
      />
      <ChargingPreferencesModal
        open={chargingPrefsModalOpen}
        onClose={() => setChargingPrefsModalOpen(false)}
      />
    </ThemeProvider>
  )
}

type Props = { vehicleChargerId: VehicleCharger['id'] }

export default function VehicleChargerScheduleSection(props: Props) {
  const { status } = chargerEnrollmentCollection.useFetch()
  const charger = useAppSelector((state) =>
    vehicleChargersCollection.selectors.selectById(
      state,
      props.vehicleChargerId,
    ),
  )

  const loadingOrWaitingFetch = useAppSelector((state) => {
    const queryStates =
      vehicleChargersCollection.selectors.queryState.selectByResourceId(
        state,
        props.vehicleChargerId,
      )

    const unfetchedStateExists = queryStates.some((queryState) =>
      ['idle', 'loading'].includes(queryState.status),
    )
    return unfetchedStateExists
  })

  const noChargerFound = !loadingOrWaitingFetch && !charger

  if (noChargerFound) {
    return null
  }

  if (loadingOrWaitingFetch || status === 'loading') {
    return (
      <div style={{ width: '100%', height: '400px' }}>
        <PageLoader />
      </div>
    )
  }

  return charger ? (
    <VehicleChargerScheduleSectionView vehicleCharger={charger} />
  ) : null
}
